import type { SetupOption } from "../../onboarding/OnboardingSetupShopContent";

import * as React from "react";

import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import useInstallShopify from "@editor/hooks/useInstallShopify";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { useModal } from "@editor/hooks/useModal";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import Button from "@replo/design-system/components/button/Button";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { ArrowRight } from "lucide-react";

import OnboardingSetupShopContent from "../../onboarding/OnboardingSetupShopContent";
import ShopDesignLibrary from "./ShopDesignLibrary";
import ShopInfo from "./ShopInfo";

const SetupStep = {
  SHOP_INFO: "shop_info",
  CONNECT_STORE: "connect_store",
  DESIGN_LIBRARY: "design_library",
} as const;

type SetupStep = (typeof SetupStep)[keyof typeof SetupStep];

function getStepFromNumber(stepNumber?: number): SetupStep | null {
  switch (stepNumber) {
    case 1:
      return SetupStep.SHOP_INFO;
    case 2:
      return SetupStep.CONNECT_STORE;
    case 3:
      return SetupStep.DESIGN_LIBRARY;
    default:
      return null;
  }
}

function getStepNumber(step: SetupStep): number | null {
  switch (step) {
    case SetupStep.SHOP_INFO:
      return 1;
    case SetupStep.CONNECT_STORE:
      return 2;
    case SetupStep.DESIGN_LIBRARY:
      return 3;
    default:
      return null;
  }
}

function CreateShopModal({
  initialStep,
  shopInfo: initialShopInfo,
  projectId,
}: {
  initialStep?: number;
  shopInfo?: any;
  projectId?: string;
} = {}) {
  const { closeModal } = useModal();
  const [step, setStep] = React.useState<SetupStep>(
    getStepFromNumber(initialStep) || SetupStep.SHOP_INFO,
  );
  const [selectedOption, setSelectedOption] =
    React.useState<SetupOption | null>("new");
  const [shopInfo, setShopInfo] = React.useState<{ name: string } | null>(
    initialShopInfo ?? null,
  );
  const [createdProjectId, setCreatedProjectId] = React.useState<
    string | undefined
  >(projectId);

  const logEvent = useLogAnalytics();
  const { user } = useCurrentUser();
  const workspaceId = useCurrentWorkspaceId();

  const installShopify = useInstallShopify();
  const { mutateAsync: createShop, isPending: isCreatingShop } =
    trpc.project.create.useMutation({
      onError: (error) => {
        if (error.data?.key === "project.integration.alreadyAssigned") {
          closeModal({ type: "createShopFlowModal" });
        }
      },
    });

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closeModal({ type: "createShopFlowModal" });
    }
  };

  const handleContinue = async (_e?: React.MouseEvent) => {
    if (step === SetupStep.SHOP_INFO) {
      setStep(SetupStep.CONNECT_STORE);
    } else if (step === SetupStep.CONNECT_STORE) {
      if (user && selectedOption) {
        logEvent("onboarding.flow.step.completed", {
          userId: user.email,
          stepId: "connect-store",
          stepType: "connect-store",
          stepData: {
            selectedOption,
          },
        });
      }

      if (!workspaceId || !shopInfo?.name) {
        console.error("Missing workspaceId or shop name");
        return;
      }

      try {
        const { project: newProject } = await createShop({
          name: shopInfo.name,
          workspaceId,
          shopifyIntegrationId: null,
        });

        logEvent("project.create", {
          withShopify: selectedOption === "import",
          createdFrom: "createShopFlowModal",
        });

        void trpcUtils.workspace.getUserWorkspacesList.invalidate();
        void trpcUtils.project.findByUserId.invalidate();
        void trpcUtils.project.listWithStats.invalidate();
        void trpcUtils.project.listGroupedByWorkspaceForUser.invalidate();

        if (!newProject) {
          console.error("Failed to create project");
          return;
        }

        if (selectedOption === "import") {
          localStorage.setItem(
            "shopCreationFlow",
            JSON.stringify({
              projectId: newProject.id,
              workspaceId: newProject.ownerWorkspaceId,
              step: getStepNumber(SetupStep.DESIGN_LIBRARY),
              shopInfo,
              timestamp: Date.now(),
            }),
          );

          await installShopify({
            cookie: {
              type: "newProject",
              projectId: newProject.id,
              workspaceId: newProject.ownerWorkspaceId,
            },
            workspaceId: newProject.ownerWorkspaceId,
          });
        } else {
          setCreatedProjectId(newProject.id);
          setStep(SetupStep.DESIGN_LIBRARY);
        }
      } catch (error) {
        console.error("Error creating shop:", error);
      }
    } else {
      closeModal({ type: "createShopFlowModal" });
    }
  };

  if (step === SetupStep.SHOP_INFO) {
    return (
      <ShopInfo
        onContinue={(info) => {
          setShopInfo(info);
          void handleContinue();
        }}
        onClose={() => closeModal({ type: "createShopFlowModal" })}
      />
    );
  }

  if (step === SetupStep.DESIGN_LIBRARY) {
    return (
      <ShopDesignLibrary
        onBack={() => setStep(SetupStep.CONNECT_STORE)}
        onContinue={() => void handleContinue()}
        projectId={createdProjectId}
      />
    );
  }

  return (
    <Modal
      isOpen={true}
      onOpenChange={handleOpenChange}
      size="custom"
      title="How do you want to set up your shop?"
      description="Let's get you up and running!"
      layoutClassName="h-[700px] w-[1000px]"
      UNSAFE_className="[&_.header-h2]:text-center [&_.header-h2]:text-3xl [&_.header-h2]:py-4 [&_.text-muted]:text-center [&_.text-muted]:pb-4 [&_[data-card-content]_.text-muted]:!text-left [&_[data-card-content]_.text-muted]:!pb-0"
      footer={
        <div className="w-full">
          <div className="border-t-[0.5px] border-border -mx-6"></div>
          <div className="flex justify-between items-center w-full pt-4">
            <div className="text-sm text-muted flex items-center h-10 leading-none -mb-4">
              Step {getStepNumber(step)} of 3
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="base"
                onClick={() => setStep(SetupStep.SHOP_INFO)}
              >
                Back
              </Button>
              <Button
                variant="primary"
                size="base"
                onClick={() => void handleContinue()}
                disabled={!selectedOption || isCreatingShop}
                isLoading={isCreatingShop}
                endEnhancer={<ArrowRight size={16} />}
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <div className="flex items-start justify-center min-h-0 flex-1 overflow-y-auto py-6 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="w-full max-w-3xl">
          <OnboardingSetupShopContent
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            isShopContext={true}
          />
        </div>
      </div>
    </Modal>
  );
}

export default CreateShopModal;
